import {
  Box,
  Container,
  Skeleton,
  Stack,
  CircularProgress,
  ThemeProvider,
} from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { GoFlyerAdminContext } from "../../../contextApi/context";
import { useFlyerReview } from "./useFlyerReview";
import { useReviewSocket } from "./useReviewSocket";
import { useFlyerSnapshot } from "./useFlyerSnapshot";
import { useFlyerItemInspector } from "./useFlyerItemInspector";
import { useReviewActivity } from "./components/ChangesByItem/useReviewActivity";
import { useFlyerItemSummaries } from "./components/ChangesByItem/useFlyerItemSummaries";
import { collectSwapPartnerIdsFromGroups } from "./components/ChangesByItem/manualSignalCr";
import { useCrSubmitter } from "./components/ChangesByItem/useCrSubmitter";
import { ReviewCommandBar } from "./components/ReviewCommandBar";
import { FlyerMetaBar } from "./components/FlyerMetaBar";
import { FlyerReviewBody } from "./components/FlyerReviewBody";
import { WhoHasAccessModal } from "./components/WhoHasAccessModal";
import { FlyerCommentsDrawer } from "./components/FlyerCommentsPanel/FlyerCommentsDrawer";
import { FullHistoryDrawer } from "./components/ChangesByItem/FullHistoryDrawer";
import { ApproveDialog } from "./components/ApproveDialog";
import { ReopenDialog } from "./components/ReopenDialog";
import { reviewTheme, reviewTokens } from "./theme";
import { reviewApiService } from "../../../utils/api";
import { handleReviewError } from "./errorHandling";
import { ReviewChangeRequest } from "./types";
import { resolveSelectedFlyerItemId } from "./selectFlyerItem";
import { reviewKeys } from "./queryKeys";
import {
  collectCrAffectedItemIds,
  invalidateFlyerItemCaches,
  invalidateFlyerMetaCaches,
  parseSocketCrAffectedItemIds,
} from "./invalidateReviewCache";
import { ReviewQueryProvider } from "./ReviewQueryProvider";
import { ReviewErrorState } from "./components/state/ReviewErrorState";

const FlyerReviewPageInner = () => {
  const { flyerId } = useParams<{ flyerId: string }>();
  const queryClient = useQueryClient();
  const authHeaders = useAuthHeaders();
  const { t } = useTranslation();
  const { state } = useContext(GoFlyerAdminContext);
  const jwt = state.user?.jwtToken;
  const review = useFlyerReview(flyerId, authHeaders);
  const flyerSnapshot = useFlyerSnapshot(flyerId, authHeaders);

  const [shareOpen, setShareOpen] = useState(false);
  const [flyerCommentsOpen, setFlyerCommentsOpen] = useState(false);
  const [fullHistoryOpen, setFullHistoryOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [selectedFlyerItemId, setSelectedFlyerItemId] = useState<
    string | undefined
  >();
  const [busyIds, setBusyIds] = useState<string[]>([]);

  const {
    groups,
    workspaceStats,
    initialLoading: activityInitialLoading,
    refreshing: activityRefreshing,
    error: activityError,
    refetch: refetchActivity,
  } = useReviewActivity(flyerId, review.reqOptions);

  const flyerItemIds = useMemo(() => {
    const ids = new Set(groups.map((g) => g.flyerItemId));
    collectSwapPartnerIdsFromGroups(groups).forEach((id) => ids.add(id));
    return Array.from(ids);
  }, [groups]);
  const summaries = useFlyerItemSummaries(flyerItemIds, review.reqOptions);
  const { item: inspectorItem, loading: inspectorLoading } =
    useFlyerItemInspector(selectedFlyerItemId, review.reqOptions);

  const setBusy = useCallback((id: string, busy: boolean) => {
    setBusyIds((prev) =>
      busy ? [...prev, id] : prev.filter((x) => x !== id),
    );
  }, []);

  const resolveSubmitter = useCrSubmitter(review.bootstrap?.audiences);

  const flyerPublicationStatus = flyerSnapshot.snapshot?.publicationStatus as
    | string
    | undefined;

  const handleHistoryAccept = useCallback(
    async (cr: ReviewChangeRequest) => {
      setBusy(cr.id, true);
      try {
        await reviewApiService.gfFlyerReviewAdminControllerAcceptChangeRequest(
          cr.id,
          review.reqOptions,
        );
        await refetchActivity();
        await invalidateFlyerItemCaches(
          queryClient,
          collectCrAffectedItemIds(cr),
        );
        toastSuccess(t(messages.Accept()));
      } catch (error) {
        await handleReviewError(error, t);
      } finally {
        setBusy(cr.id, false);
      }
    },
    [review.reqOptions, refetchActivity, setBusy, t, queryClient],
  );

  const handleHistoryReject = useCallback(
    async (cr: ReviewChangeRequest) => {
      setBusy(cr.id, true);
      try {
        await reviewApiService.gfFlyerReviewAdminControllerRejectChangeRequest(
          cr.id,
          review.reqOptions,
        );
        await refetchActivity();
        toastSuccess(t(messages.Reject()));
      } catch (error) {
        await handleReviewError(error, t);
      } finally {
        setBusy(cr.id, false);
      }
    },
    [review.reqOptions, refetchActivity, setBusy, t],
  );

  const handleHistoryRestore = useCallback(
    async (cr: ReviewChangeRequest) => {
      setBusy(cr.id, true);
      try {
        await reviewApiService.gfFlyerReviewAdminControllerRestoreChangeRequest(
          cr.id,
          review.reqOptions,
        );
        await refetchActivity();
        await invalidateFlyerItemCaches(
          queryClient,
          collectCrAffectedItemIds(cr),
        );
        toastSuccess(t(messages.Restored()));
      } catch (error) {
        await handleReviewError(error, t);
      } finally {
        setBusy(cr.id, false);
      }
    },
    [review.reqOptions, refetchActivity, setBusy, t, queryClient],
  );

  const { connected, presence } = useReviewSocket(
    review.bootstrap?.session?.id,
    jwt,
    {
      onCommentChange: () => {
        if (flyerId) {
          void queryClient.invalidateQueries({
            queryKey: reviewKeys.comments(flyerId),
          });
        }
      },
      onChangeRequestChange: (payload) => {
        if (!flyerId) return;
        void queryClient.invalidateQueries({
          queryKey: reviewKeys.changeRequests(flyerId),
        });
        const itemIds = parseSocketCrAffectedItemIds(payload);
        if (itemIds.length > 0) {
          void invalidateFlyerItemCaches(queryClient, itemIds);
        }
      },
      onBootstrapChange: () => {
        if (flyerId) {
          void invalidateFlyerMetaCaches(queryClient, flyerId);
        }
      },
    },
  );

  useEffect(() => {
    if (activityInitialLoading) return;
    setSelectedFlyerItemId((prev) =>
      resolveSelectedFlyerItemId(prev, groups),
    );
  }, [activityInitialLoading, groups]);

  const handleExportReport = async () => {
    if (!flyerId) return;
    try {
      const base = process.env.REACT_APP_GOFLYER_API_END_POINT || "";
      const response = await fetch(
        `${base}/admin-flyer/${flyerId}/review/change-report`,
        { headers: authHeaders.headers },
      );
      if (!response.ok) throw response;
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flyer-${flyerId}-change-report.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    }
  };

  if (!flyerId) {
    return null;
  }

  return (
    <ThemeProvider theme={reviewTheme}>
      <Box
        data-testid="flyer-review-page"
        component="main"
        sx={{
          bgcolor: "background.default",
          minHeight: "100%",
          color: "text.primary",
          fontFamily: reviewTokens.fontFamily,
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
            px: { xs: 1.5, sm: 2.5 },
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          {review.bootstrapLoading ? (
            <Stack direction="row" spacing={2} alignItems="center" width="100%">
              <Skeleton width={120} height={28} />
              <Box flex={1} />
              <Skeleton width={100} height={32} />
              <Skeleton width={32} height={32} variant="circular" />
            </Stack>
          ) : (
            <ReviewCommandBar
              session={review.bootstrap?.session}
              onShare={() => setShareOpen(true)}
              onOpenFlyerComments={() => setFlyerCommentsOpen(true)}
              onApprove={() => setApproveOpen(true)}
              onReopen={() => setReopenOpen(true)}
              onExportReport={handleExportReport}
              connected={connected}
              presence={presence}
              bootstrapLoading={review.bootstrapLoading}
            />
          )}
        </Box>

        <Container maxWidth="xl" sx={{ py: 2 }}>
          <FlyerMetaBar
            flyerId={flyerId}
            snapshot={flyerSnapshot.snapshot}
            snapshotLoading={flyerSnapshot.loading}
            snapshotError={flyerSnapshot.error}
            onRetrySnapshot={flyerSnapshot.refetch}
            bootstrap={review.bootstrap}
            bootstrapLoading={review.bootstrapLoading}
            workspaceStats={workspaceStats}
            onManageAccess={() => setShareOpen(true)}
            flyerCommentsOpen={flyerCommentsOpen}
            onToggleFlyerComments={() =>
              setFlyerCommentsOpen((open) => !open)
            }
            onOpenFullHistory={() => setFullHistoryOpen(true)}
            itemListId={
              inspectorItem?.flyerItemList?.id ??
              flyerSnapshot.snapshot?.flyerItemListId
            }
          />

          {activityInitialLoading ? (
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              alignItems="stretch"
            >
              <Skeleton
                variant="rounded"
                sx={{ width: { xs: "100%", lg: 260 }, height: 360 }}
              />
              <Skeleton
                variant="rounded"
                sx={{ flex: 1, height: 360 }}
              />
              <Skeleton
                variant="rounded"
                sx={{ width: { xs: "100%", lg: 320 }, height: 360 }}
              />
            </Stack>
          ) : activityError ? (
            <ReviewErrorState
              card
              testId="review-activity-error"
              message={t(
                messages.There_is_an_error_please_Contact_Administrator(),
              )}
              retryLabel={t(messages.Review_Retry_Load_Flyer())}
              retrying={activityRefreshing}
              onRetry={() => {
                void refetchActivity();
              }}
            />
          ) : (
            <Box sx={{ position: "relative" }}>
              {/*
                Background-refresh indicator. Absolutely positioned and out of
                document flow on purpose: a socket update triggers a background
                refetch (isFetching, not isLoading), and if this spinner took
                flow space it would push the whole review body down while
                fetching and snap it back up on completion — the body would
                "jump" on every socket update. As an overlay it can't reflow
                anything. pointer-events: none so it never blocks the body.
              */}
              {activityRefreshing && (
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    zIndex: 5,
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress size={16} />
                </Box>
              )}
              <FlyerReviewBody
                flyerId={flyerId}
                reqOptions={review.reqOptions}
                bootstrap={review.bootstrap}
                flyerPublicationStatus={flyerPublicationStatus}
                groups={groups}
                summaries={summaries}
                selectedFlyerItemId={selectedFlyerItemId}
                onSelectItem={setSelectedFlyerItemId}
                inspectorItem={inspectorItem}
                inspectorLoading={inspectorLoading}
                busyIds={busyIds}
                setBusy={setBusy}
                onActivityRefetch={refetchActivity}
              />
            </Box>
          )}
        </Container>

        <FlyerCommentsDrawer
          open={flyerCommentsOpen}
          onClose={() => setFlyerCommentsOpen(false)}
          flyerId={flyerId}
          reqOptions={review.reqOptions}
          bootstrap={review.bootstrap}
        />

        <FullHistoryDrawer
          open={fullHistoryOpen}
          onClose={() => setFullHistoryOpen(false)}
          groups={groups}
          summaries={summaries}
          flyerPublicationStatus={flyerPublicationStatus}
          resolveSubmitter={resolveSubmitter}
          busyIds={busyIds}
          onAccept={handleHistoryAccept}
          onReject={handleHistoryReject}
          onRestore={handleHistoryRestore}
        />

        <WhoHasAccessModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          bootstrap={review.bootstrap}
          inviteAudience={review.inviteAudience}
          updateAudience={review.updateAudience}
          regenerateAudienceToken={review.regenerateAudienceToken}
          revokeAudience={review.revokeAudience}
          setAnyoneLink={review.setAnyoneLink}
        />
        <ApproveDialog
          open={approveOpen}
          onClose={() => setApproveOpen(false)}
          onConfirm={review.approve}
        />
        <ReopenDialog
          open={reopenOpen}
          onClose={() => setReopenOpen(false)}
          onReopen={review.reopen}
          onRestorePermissions={review.restorePermissions}
        />
      </Box>
    </ThemeProvider>
  );
};

const FlyerReviewPage = () => (
  <ReviewQueryProvider>
    <FlyerReviewPageInner />
  </ReviewQueryProvider>
);

export default FlyerReviewPage;
